import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  imageUrl?: string;
  url?: string;
  type?: 'website' | 'article';
  structuredData?: Record<string, unknown> | Array<Record<string, unknown>>;
}

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  keywords,
  imageUrl,
  url,
  type = 'website',
  structuredData,
}) => {
  const siteName = 'mapleos';
  const siteUrl = 'https://mapleos.ai';
  const fullTitle = `${title} | ${siteName}`;
  const fullUrl = url ? `${siteUrl}${url}` : siteUrl;
  const fullImageUrl = imageUrl
    ? imageUrl.startsWith('http')
      ? imageUrl
      : `${siteUrl}${imageUrl}`
    : undefined;

  React.useEffect(() => {
    document.dispatchEvent(new Event('prerender-ready'));
  }, [title, description, keywords, imageUrl, url, type, structuredData]);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <link rel="canonical" href={fullUrl} />
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="robots" content="index,follow" />

      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="en_US" />
      {fullImageUrl && <meta property="og:image" content={fullImageUrl} />}

      <meta name="twitter:card" content={fullImageUrl ? 'summary_large_image' : 'summary'} />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      {fullImageUrl && <meta name="twitter:image" content={fullImageUrl} />}
      <meta name="twitter:site" content="@mapleosai" />

      {structuredData && (
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      )}
    </Helmet>
  );
};

export default SEO;